import * as qs from 'querystring';
import * as Request from './request';
import * as Model from './model';
import client from './client';

// #region Auth
const authToken = (username: string, password: string): Promise<Model.AuthResponse> => {
    const params = {
        username,
        password
    };
    return client.post<Model.AuthResponse>('oauth2/token', qs.stringify(params))
        .then(res => res.data);
};
// #endregion

// #region Continents
const continents = (query: string | null = null): Promise<Model.Continent[]> => {
    const params = {
        query
    };
    return client.get<Model.Continent[]>('continents', { params })
        .then(res => res.data);
}

const continentAdd = (data: Request.Continent): Promise<Model.Continent> => {
    return client.post<Model.Continent>('continents', data)
        .then(res => res.data);
}

const continentEdit = (id: number, data: Request.Continent): Promise<Model.Continent> => {
    return client.post<Model.Continent>(`continents/${id}`, data)
        .then(res => res.data);
}

const continentDelete = (id: number): Promise<void> => {
    return client.delete(`continents/${id}`)
        .then(res => res.data);
}
// #endregion

// #region Countries
const countries = (continent: null | number = null, query: string | null = null): Promise<Model.CountryInfo[]> => {
    const params = {
        continent,
        query
    };
    return client.get<Model.CountryInfo[]>('countries', { params })
        .then(res => res.data);
}

const countriesGrouped = (): Promise<Model.Countries[]> => {
    return client.get<Model.Countries[]>('countries/grouped')
        .then(res => res.data);
}

const countryAdd = (data: Request.Country): Promise<Model.CountryInfo> => {
    return client.post<Model.CountryInfo>('countries', data)
        .then(res => res.data);
}

const countryEdit = (id: number, data: Request.Country): Promise<Model.CountryInfo> => {
    return client.post<Model.CountryInfo>(`countries/${id}`, data)
        .then(res => res.data);
}

const countryDelete = (id: number): Promise<void> => {
    return client.delete(`countries/${id}`)
        .then(res => res.data);
}
// #endregion

// #region Regions
const regions = (country: null | number = null, query: string | null = null): Promise<Model.RegionInfo[]> => {
    const params = {
        country,
        query
    };
    return client.get<Model.RegionInfo[]>('regions', { params })
        .then(res => res.data);
}

const regionAdd = (data: Request.Region): Promise<Model.RegionInfo> => {
    return client.post<Model.RegionInfo>('regions', data)
        .then(res => res.data);
}

const regionEdit = (id: number, data: Request.Region): Promise<Model.RegionInfo> => {
    return client.post<Model.RegionInfo>(`regions/${id}`, data)
        .then(res => res.data);
}

const regionDelete = (id: number): Promise<void> => {
    return client.delete(`regions/${id}`)
        .then(res => res.data);
}
// #endregion

// #region Subregions
const subregions = (region: null | number = null, query: string | null = null): Promise<Model.SubregionInfo[]> => {
    const params = {
        region,
        query
    };
    return client.get<Model.SubregionInfo[]>('subregions', { params })
        .then(res => res.data);
}

const subregionAdd = (data: Request.Subregion): Promise<Model.SubregionInfo> => {
    return client.post<Model.SubregionInfo>('subregions', data)
        .then(res => res.data);
}

const subregionEdit = (id: number, data: Request.Subregion): Promise<Model.SubregionInfo> => {
    return client.post<Model.SubregionInfo>(`subregions/${id}`, data)
        .then(res => res.data);
}

const subregionDelete = (id: number): Promise<void> => {
    return client.delete(`subregions/${id}`)
        .then(res => res.data);
}
// #endregion

// #region Appellations
const appellations = (query: string | null = null): Promise<Model.Appellation[]> => {
    const params = {
        query
    };
    return client.get<Model.Appellation[]>('appellations', { params })
        .then(res => res.data);
}

const appellationAdd = (data: Request.Appellation): Promise<Model.Appellation> => {
    return client.post<Model.Appellation>('appellations', data)
        .then(res => res.data);
}

const appellationEdit = (id: number, data: Request.Appellation): Promise<Model.Appellation> => {
    return client.post<Model.Appellation>(`appellations/${id}`, data)
        .then(res => res.data);
}

const appellationDelete = (id: number): Promise<void> => {
    return client.delete(`appellations/${id}`)
        .then(res => res.data);
}
// #endregion

// #region Classifications
const classifications = (query: string | null = null): Promise<Model.Classification[]> => {
    const params = {
        query
    };
    return client.get<Model.Classification[]>('classifications', { params })
        .then(res => res.data);
}

const classificationAdd = (data: Request.Classification): Promise<Model.Classification> => {
    return client.post<Model.Classification>('classifications', data)
        .then(res => res.data);
}

const classificationEdit = (id: number, data: Request.Classification): Promise<Model.Classification> => {
    return client.post<Model.Classification>(`classifications/${id}`, data)
        .then(res => res.data);
}

const classificationDelete = (id: number): Promise<void> => {
    return client.delete(`classifications/${id}`)
        .then(res => res.data);
}
// #endregion

// #region RedVarieties
const redVarieties = (query: string | null = null): Promise<Model.RedVariety[]> => {
    const params = {
        query
    };
    return client.get<Model.RedVariety[]>('redVarieties', { params })
        .then(res => res.data);
}

const redVarietyAdd = (data: Request.RedVariety): Promise<Model.RedVariety> => {
    return client.post<Model.RedVariety>('redVarieties', data)
        .then(res => res.data);
}

const redVarietyEdit = (id: number, data: Request.RedVariety): Promise<Model.RedVariety> => {
    return client.post<Model.RedVariety>(`redVarieties/${id}`, data)
        .then(res => res.data);
}

const redVarietyDelete = (id: number): Promise<void> => {
    return client.delete(`redVarieties/${id}`)
        .then(res => res.data);
}
// #endregion

// #region WhiteVarieties
const whiteVarieties = (query: string | null = null): Promise<Model.WhiteVariety[]> => {
    const params = {
        query
    };
    return client.get<Model.WhiteVariety[]>('whiteVarieties', { params })
        .then(res => res.data);
}

const whiteVarietyAdd = (data: Request.WhiteVariety): Promise<Model.WhiteVariety> => {
    return client.post<Model.WhiteVariety>('whiteVarieties', data)
        .then(res => res.data);
}

const whiteVarietyEdit = (id: number, data: Request.WhiteVariety): Promise<Model.WhiteVariety> => {
    return client.post<Model.WhiteVariety>(`whiteVarieties/${id}`, data)
        .then(res => res.data);
}

const whiteVarietyDelete = (id: number): Promise<void> => {
    return client.delete(`whiteVarieties/${id}`)
        .then(res => res.data);
}
// #endregion

// #region Photo
const photoAdd = (photo: File): Promise<Model.Photo> => {
    const data = new FormData();
    data.append('photo', photo);
    return client.post<Model.Photo>('photos/', data)
        .then(res => res.data);
}

const photoDelete = (id: number, filename: string): Promise<void> => {
    return client.delete(`photos/${id}/${filename}`)
        .then(res => res.data);
}
// #endregion

// #region Wineries
const wineries = (): Promise<Model.Winery[]> => {
    return client.get<Model.Winery[]>('wineries')
        .then(res => res.data);
}

const wineryAdd = (data: Request.Winery): Promise<Model.Winery> => {
    return client.post<Model.Winery>('wineries', data)
        .then(res => res.data);
}

const wineryEdit = (id: number, data: Request.Winery): Promise<Model.Winery> => {
    return client.post<Model.Winery>(`wineries/${id}`, data)
        .then(res => res.data);
}

const wineryDelete = (id: number): Promise<void> => {
    return client.delete(`wineries/${id}`)
        .then(res => res.data);
}
// #endregion

export const API = {
    auth: {
        token: authToken
    },
    continent: {
        list: continents,
        add: continentAdd,
        edit: continentEdit,
        delete: continentDelete
    },
    country: {
        list: countries,
        grouped: countriesGrouped,
        add: countryAdd,
        edit: countryEdit,
        delete: countryDelete
    },
    region: {
        list: regions,
        add: regionAdd,
        edit: regionEdit,
        delete: regionDelete
    },
    subregion: {
        list: subregions,
        add: subregionAdd,
        edit: subregionEdit,
        delete: subregionDelete
    },
    appellation: {
        list: appellations,
        add: appellationAdd,
        edit: appellationEdit,
        delete: appellationDelete
    },
    classification: {
        list: classifications,
        add: classificationAdd,
        edit: classificationEdit,
        delete: classificationDelete
    },
    redVariety: {
        list: redVarieties,
        add: redVarietyAdd,
        edit: redVarietyEdit,
        delete: redVarietyDelete
    },
    whiteVariety: {
        list: whiteVarieties,
        add: whiteVarietyAdd,
        edit: whiteVarietyEdit,
        delete: whiteVarietyDelete
    },
    photo: {
        add: photoAdd,
        delete: photoDelete
    },
    winery: {
        list: wineries,
        add: wineryAdd,
        edit: wineryEdit,
        delete: wineryDelete
    }
};



// WEBPACK FOOTER //
// ./src/api/api.ts